import React, { useState } from 'react';
import { Box, Card, CardContent, Container, Grid, Typography, Button, CircularProgress, Divider, TextField, InputAdornment, IconButton } from '@material-ui/core';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { FormTextField } from 'reactmuicommon';
import { Api, showToast, thousandSeparatorWithTLSeperator } from 'core';
import { useDispatch } from 'react-redux';
import FileCopyIcon from '@material-ui/icons/FileCopy';

const schema = yup.object({
  amount: yup.string().required('Tutar zorunludur!'),
  customerName: yup.string(),
  customerPhone: yup.string().transform((v) => v.replaceAll('_', '').replaceAll('-', '')).length(10, '10 haneli şekilde giriniz'),
  description: yup.string(),
});

const CustomerPaymentLinkCreatePage: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [createdLink, setCreatedLink] = useState<string | null>(null);

  const onSubmit = async (values: any, formikBag: any) => {
    setLoading(true);
    try {
      // 1. Get paymentTransactionId via background login
      const authRes = await Api.Auth.login({ isForIframe: true });
      let paymentTransactionId = authRes.result?.paymentTransactionId;
      
      if (!paymentTransactionId) {
        // Fallback or retry
        dispatch(showToast({ message: 'Transaction ID alınamadı.', severity: 'error' }));
        setLoading(false);
        return;
      }

      // 2. Create link
      const requestDto = {
        amount: Number(values.amount),
        paymentTransactionId: paymentTransactionId,
        customerName: values.customerName,
        customerPhone: values.customerPhone?.replaceAll('-', '').replaceAll('_', ''),
        description: values.description,
        expiresInHours: 12
      };

      const res = await Api.PaymentLink.create(requestDto);
      if (res.isSuccess && res.result) {
        // Müşteriye gönderilecek URL: https://bayi.occopay.com/pay/:token
        // Use current origin
        const url = `${window.location.origin}/pay/${res.result.token}`;
        setCreatedLink(url);
        dispatch(showToast({ message: 'Müşteri ödeme linki başarıyla oluşturuldu!', severity: 'success' }));
        formikBag.resetForm();
      } else {
        dispatch(showToast({ message: res.errorMessage || 'Link oluşturulamadı.', severity: 'error' }));
      }
    } catch (err: any) {
      dispatch(showToast({ message: err.message || 'Bir hata oluştu.', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (createdLink) {
      navigator.clipboard.writeText(createdLink);
      dispatch(showToast({ message: 'Link kopyalandı!', severity: 'success' }));
    }
  };

  return (
    <Container maxWidth="md" style={{ marginTop: 64, marginBottom: 64 }}>
      <Typography variant="h4" gutterBottom>Müşteri Ödeme Linki Oluştur</Typography>
      <Divider style={{ marginBottom: 24 }} />
      <Card>
        <CardContent>
          <Formik
            initialValues={{ amount: '', customerName: '', customerPhone: '', description: '' }}
            validationSchema={schema}
            onSubmit={onSubmit}
          >
            {({ values, handleChange, errors, setFieldValue }) => (
              <Form>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormTextField
                      name="amount"
                      label="Tutar (₺)"
                      value={values.amount}
                      onChange={(e) => setFieldValue('amount', parseInt(e.target.value, 10) || undefined)}
                      error={!!errors.amount}
                      helperText={errors.amount as string}
                      required
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        inputComponent: thousandSeparatorWithTLSeperator as any,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormTextField
                      name="customerName"
                      label="Müşteri Ad Soyad (Opsiyonel)"
                      value={values.customerName}
                      onChange={handleChange}
                      fullWidth
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormTextField
                      name="customerPhone"
                      label="Müşteri Telefonu (Opsiyonel)"
                      value={values.customerPhone}
                      onChange={handleChange}
                      error={!!errors.customerPhone}
                      helperText={errors.customerPhone as string}
                      fullWidth
                      variant="outlined"
                      placeholder="5551234567"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormTextField
                      name="description"
                      label="Açıklama (Opsiyonel)"
                      value={values.description}
                      onChange={handleChange}
                      fullWidth
                      variant="outlined"
                      placeholder="Örn: 34ABC123 Plakalı Araç Peşinatı"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                      fullWidth
                      size="large"
                    >
                      {loading ? <CircularProgress size={24} /> : 'Linki Oluştur'}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>

          {createdLink && (
            <Box mt={4} p={3} bgcolor="#f0fdf4" borderRadius={8} border="1px solid #84cc16">
              <Typography variant="h6" color="primary" gutterBottom>Link Başarıyla Oluşturuldu!</Typography>
              <Typography variant="body2" gutterBottom>Aşağıdaki linki kopyalayıp müşterinize (WhatsApp, SMS, Email vb.) gönderebilirsiniz:</Typography>
              <TextField
                value={createdLink}
                fullWidth
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={copyToClipboard} color="primary">
                        <FileCopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default CustomerPaymentLinkCreatePage;
